import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useApolloClient } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import type { Variant } from '@storefront/lib-global/codegen/types';
import { StockStatus } from '@storefront/lib-global/codegen/types';
import { useLocaleContext, useOverlayContext } from '@storefront/lib-global/context';
import { FocusTrap } from '@storefront/lib-global/globalComponents/focusTrap/FocusTrap';
import { useDisableBodyScroll } from '@storefront/lib-global/hooks/useDisableBodyScroll';
import { useEscapeKeyListener } from '@storefront/lib-global/hooks/useEscapeKeyListener';
import { Close } from '@storefront/lib-global/icons';
import { createClassName } from '@storefront/lib-global/utils/createClassName';
import { SizeAndWidthContextProvider } from '../../../context';
import { getProductVariants } from '../../../services/getProductVariants';
import { buildUniqueSizes, buildUniqueWidths } from '../../../utils';
import { isOutOfStock } from '../../../utils/isOutOfStock';
import { ErrorMessage } from '../errorMessage/ErrorMessage';
import {
  CloseButton,
  CloseButtonContainer,
  QuickAddToBagContainer,
  QuickAddToBagOverlay,
} from './QuickAddToBag.styles';
import { QuickAddToBagSizeAndWidthSelectors } from './QuickAddToBagSizeAndWidthSelector';

interface ProductData {
  key?: string;
  variants?: Variant[];
}

interface QuickAddToBagProps {
  productSku: string;
  productName?: string;
  isOpen: boolean;
  onClose: () => void;
}

export function QuickAddToBag({ productSku, isOpen, onClose, productName }: QuickAddToBagProps) {
  const apolloClient: ApolloClient<object> = useApolloClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('lib-products-common');

  const { locale } = useLocaleContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isError, setError] = useState(false);
  const [productData, setProductData] = useState<ProductData | null>(null);
  const { isMiniCartOpen } = useOverlayContext();

  const { variants = [] } = productData || {};

  const uniqueSizes = useMemo(() => buildUniqueSizes(variants), [variants]);
  const uniqueWidths = useMemo(() => buildUniqueWidths(variants), [variants]);
  const initialWidth = useMemo(() => (uniqueWidths.length ? uniqueWidths[0] : ''), [uniqueWidths]);

  const handleClose = () => {
    setIsClosing(true);

    setTimeout(() => {
      setIsOverlayVisible(false);
      setIsClosing(false);
      onClose();
    }, 300);
  };

  useEscapeKeyListener(async () => {
    handleClose();
  });

  useDisableBodyScroll(isOpen);

  const fetchProductData = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await getProductVariants(apolloClient, {
        input: {
          key: productSku,
          locale,
        },
      });

      if (result.error) {
        throw result.error;
      }

      if (result.data) {
        setProductData({
          key: productSku,
          variants: result.data,
        });
      }
    } catch (err) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [apolloClient, locale, productSku]);

  useEffect(() => {
    if (isOpen) {
      setIsOverlayVisible(true);
      setIsClosing(false);
      fetchProductData();
    }
  }, [isOpen, fetchProductData]);

  if ((!isOpen && !isOverlayVisible) || isLoading) {
    return null;
  }

  return (
    <>
      {isOverlayVisible && !isMiniCartOpen && <QuickAddToBagOverlay onClick={handleClose} />}
      <FocusTrap>
        <QuickAddToBagContainer
          role="dialog"
          aria-modal
          ref={containerRef}
          className={createClassName({ 'is-closing': isClosing })}
          data-testid="quickAddToBag"
          aria-label={t('quick.add.to.bag')}
        >
          <CloseButtonContainer>
            <CloseButton onClick={handleClose} aria-label={t('quick.add.to.bag.close')} data-testid="qatbProductClose">
              <Close viewBox="0 -1 20 20" />
            </CloseButton>
          </CloseButtonContainer>
          {variants && variants.length > 0 && (
            <SizeAndWidthContextProvider
              variants={variants.map((variant) => ({
                ...variant,
                stock: variant.stock || {
                  status: isOutOfStock(variant.size || '', variant.fitLabel || '', variants)
                    ? StockStatus.OUTOFSTOCK
                    : StockStatus.INSTOCK,
                },
              }))}
              uniqueSizes={uniqueSizes}
              uniqueWidths={uniqueWidths}
              initialSize=""
              initialWidth={initialWidth}
            >
              <QuickAddToBagSizeAndWidthSelectors onClose={handleClose} productName={productName} />
            </SizeAndWidthContextProvider>
          )}
          {isError && <ErrorMessage>{t('pdp.add.to.bag.error')}</ErrorMessage>}
        </QuickAddToBagContainer>
      </FocusTrap>
    </>
  );
}
